import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader, AlertCircle, CheckCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import AuthShell from "../components/auth/AuthShell";
import { isValidEmail, normalizeEmail } from "../utils/authValidation";
import { getFriendlyAuthError } from "../utils/authErrorMessages";
import { errorDev } from '../utils/logger.js';

function Feedback({ type = "error", message }) {
  if (!message) return null;

  const isError = type === "error";
  const Icon = isError ? AlertCircle : CheckCircle;
  const boxClass = isError
    ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300"
    : "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300";

  return (
    <div className={`rounded-xl border p-3 text-sm flex items-start gap-2 ${boxClass}`} role={isError ? "alert" : "status"}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span>{message}</span>
    </div>
  );
}

export default function ForgotPassword() {
  const { t, ready: i18nReady } = useTranslation(["auth", "common"]);
  const safeT = (key, fallback) => {
    if (!i18nReady) return fallback || key;
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch (err) {
      errorDev("Operation failed:", err);
      return fallback || key;
    }
  };

  const { sendPasswordReset, error: authError } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess(false);

    const normalized = normalizeEmail(email);
    if (!normalized) {
      setError(safeT("auth.errors.emailRequired", "Email address is required."));
      return;
    }

    if (!isValidEmail(normalized)) {
      setError(safeT("auth.errors.invalidEmail", "Please enter a valid email address."));
      return;
    }

    try {
      setBusy(true);
      await sendPasswordReset(normalized);
      setSuccess(true);
      setEmail("");
    } catch (err) {
      if (err?.code === "auth/user-not-found") {
        setSuccess(true);
        setEmail("");
      } else {
        setError(
          authError ||
            getFriendlyAuthError(err, t, {
              defaultKey: "auth.errors.resetFailed",
              defaultMessage: "We could not send a reset link right now. Please try again.",
            })
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title={safeT("auth.forgotPassword", "Forgot Password")}
      subtitle={safeT("auth.resetDescription", "Reset access securely and continue with Orrith.")}
      helperLinks={[
        { to: "/login", label: safeT("auth.signIn", "Sign In") },
        { to: "/signup", label: safeT("auth.signup", "Register") },
      ]}
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div>
          <label htmlFor="forgot-email" className="block text-sm font-medium text-gray-800 dark:text-gray-100 mb-1.5">
            {safeT("auth.email", "Email Address")}
          </label>
          <input
            id="forgot-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            aria-invalid={Boolean(error)}
            className="ui-input"
          />
        </div>

        <Feedback type="error" message={error} />
        <Feedback
          type="success"
          message={
            success
              ? safeT(
                  "auth.resetEmailSent",
                  "If this email is registered, a reset link has been sent. Check your inbox and spam folder."
                )
              : ""
          }
        />

        <button
          type="submit"
          disabled={busy}
          className="ui-btn-primary w-full py-2.5"
        >
          {busy && <span className="orrith-loader orrith-loader-xs orrith-loader-on-dark" aria-hidden="true" />}
          {safeT("auth.sendReset", "Send Reset Link")}
        </button>
      </form>

      <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 text-center">
        <span>{safeT("auth.haveAccount", "Already have an account?")} </span>
        <Link to="/login" className="text-green-700 dark:text-green-400 font-semibold hover:text-green-800 dark:hover:text-green-300">
          {safeT("auth.signIn", "Sign In")}
        </Link>
      </p>
      <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
        {safeT("auth.termsAgreementPrefix", "By continuing, you acknowledge that you have read and agree to Orrith’s")} {" "}
        <Link to="/legal/terms" className="text-green-700 dark:text-green-400 hover:underline font-medium">
          {safeT("auth.rulesAndRegulations", "Rules & Regulations")}
        </Link>
        .
      </p>
    </AuthShell>
  );
}
